import { slideUp, slideDown } from '../../js/theme/helpers';
import { Stat } from '../stats/script';

/**
 * Represents a Tabs component.
 */
class Tabs {
	/**
	 * The index of the currently active panel.
	 * @type {number}
	 */
	currentPanelIndex = 0;

	/**
	 * Indicates whether the tabs are displayed vertically.
	 * @type {boolean}
	 */
	isVertical = false;

	/**
	 * Creates an instance of Tabs.
	 * @param {HTMLElement} el - The root element of the Tabs component.
	 * @param {Object} options - Additional options for the Tabs component.
	 */
	constructor(el, options = {}) {
		if (el) {
			this.el = el;
			this.tabsEl = this.el.querySelector('.wp-block-embark-tabs__tablist');
			this.panels = [...this.el.querySelectorAll('.wp-block-embark-tabpanel')];
			this.mediaContainer = this.el.querySelector('.wp-block-embark-tabs__media');
			this.panelImageMap = new Map(); // Maps panel index to media image element
			this.options = {
				accordionSpeed: 300,
				breakpoint: 'md',
				autoCloseSiblings: false,
				...options,
			};

			if (this.el.classList.contains('is-style-vertical')) {
				this.isVertical = true;
			}

			// Initialize media images for background-cover style
			this.initializeMediaImages();

			// Set initial active state for media images (first image by default)
			if (this.panelImageMap.size > 0) {
				this.updateMediaActiveState(0);
			}

			// Duplicate tab controls within tabpanels and append them to the tablist element.
			if (this.tabsEl && this.panels.length) {
				this.panels.forEach((panel, i) => {
					const tab = panel.querySelector('.wp-block-embark-tabs__control');

					const tabClone = tab.cloneNode(true);
					tabClone.removeAttribute('id');
					this.tabsEl.appendChild(tabClone);

					setTimeout(() => {
						tabClone.classList.add('animate');
					}, (i + 1) * 100);
				});
			}

			// Add click handlers to all tab controls.
			this.tabs = [...this.el.querySelectorAll('.wp-block-embark-tabs__control')];
			this.tabs.forEach((tab) => {
				tab.addEventListener('click', this.handleTabClick);
				tab.addEventListener('keydown', this.handleKeydown);
			});

			window.addEventListener('resize', this.handleResize);
			window.addEventListener('hashchange', this.maybeOpenTabByHash);

			// Handle the initial state of the tabs.
			if (this.useTabs()) {
				// If there is a hash in the URL, open the corresponding tab.
				this.maybeOpenTabByHash();

				// If there is no hash, open the first tab.
				if (this.currentPanelIndex === 0) {
					this.open(this.tabs[0], false);
				}
			}

			// Set equal heights for background-cover style (if applicable)
			setTimeout(() => {
				this.setEqualTabPanelHeight();
			}, 100);
		}
	}

	/**
	 * Initializes media images for background-cover style.
	 * Finds images in each tabpanel and clones them to the media container.
	 */
	initializeMediaImages = () => {
		if (!this.mediaContainer || !this.panels || this.panels.length === 0) {
			return;
		}

		// Clear any existing content in the media container
		this.mediaContainer.innerHTML = '';

		// Loop through each panel and find images
		this.panels.forEach((panel, index) => {
			const imageContainer = panel.querySelector('.wp-block-embark-tabpanel__image');

			if (imageContainer) {
				const img = imageContainer.querySelector('img');

				if (img) {
					// Clone the entire image container to preserve structure
					const imageClone = imageContainer.cloneNode(true);
					imageClone.classList.remove('wp-block-embark-tabpanel__image');
					imageClone.classList.add('wp-block-embark-tabs__media-item');

					// Append to media container
					this.mediaContainer.appendChild(imageClone);

					// Map this panel index to the cloned image element
					this.panelImageMap.set(index, imageClone);
				}
			}
		});
	};

	/**
	 * Updates the active state of media images based on the active panel.
	 * @param {number} activePanelIndex - The index of the currently active panel.
	 */
	updateMediaActiveState = (activePanelIndex) => {
		if (!this.mediaContainer || this.panelImageMap.size === 0) {
			return;
		}

		// Remove active class from all media images
		this.panelImageMap.forEach((imageElement) => {
			imageElement.classList.remove('active');
		});

		// Add active class to the corresponding image (if it exists)
		const activeImage = this.panelImageMap.get(activePanelIndex);
		if (activeImage) {
			activeImage.classList.add('active');
		}
	};

	/**
	 * Retrieves the panel associated with the given tab.
	 * @param {HTMLElement} tab - The tab element.
	 * @returns {HTMLElement|boolean} The panel element or false if not found.
	 */
	getPanelFromTab = (tab) => {
		if (tab) {
			const panelId = tab.getAttribute('aria-controls');
			return document.getElementById(panelId);
		}

		return false;
	};

	/**
	 * Closes the specified tab.
	 * @param {HTMLElement} tab - The tab element to close.
	 */
	close = (tab) => {
		if (tab) {
			const panel = this.getPanelFromTab(tab);

			if (panel) {
				panel.classList.remove('active');
				tab.setAttribute('aria-selected', 'false');
				tab.setAttribute('tabindex', '-1');

				const controls = tab.getAttribute('aria-controls');
				const tabInner = document.querySelector(`#${controls}`).querySelector('.wp-block-embark-tabpanel__inner');
				if (tabInner && this.useAccordions()) {
					slideUp(tabInner, `${this.options.accordionSpeed}ms`);
				}
			}
		}
	};

	/**
	 * Opens the specified tab.
	 * @param {HTMLElement} tab - The tab element to open.
	 * @param {boolean} [setFocus=true] - Whether to set focus to the opened tab.
	 */
	open = (tab, setFocus = true) => {
		if (tab) {
			const panel = this.getPanelFromTab(tab);
			if (panel) {
				// Close the previous tab.
				if (this.useTabs() || (this.useAccordions && this.options.autoCloseSiblings)) {
					const prevTab = this.el.querySelector('.wp-block-embark-tabs__control[aria-selected="true"]');
					this.close(prevTab);
				}

				// Open the new one.
				panel.classList.add('active');
				tab.setAttribute('aria-selected', 'true');
				tab.removeAttribute('tabindex');

				if (setFocus) {
					tab.focus();
				}

				// Update the tab index by finding which panel this tab controls.
				this.panels.forEach((_panel, i) => {
					if (panel === _panel) {
						this.currentPanelIndex = i;
					}
				});

				// Update media active state for background-cover style
				this.updateMediaActiveState(this.currentPanelIndex);

				const controls = tab.getAttribute('aria-controls');
				const tabInner = document.querySelector(`#${controls}`).querySelector('.wp-block-embark-tabpanel__inner');
				if (tabInner && this.useAccordions()) {
					slideDown(tabInner, `${this.options.accordionSpeed}ms`);
				}

				if (!panel.classList.contains('stats-init')) {
					const statsBlock = panel.querySelector('.wp-block-embark-stats');
					if (statsBlock) {
						const statDelay = parseInt(statsBlock.style.transitionDelay || 0);
						const stats = [...statsBlock.querySelectorAll('.js-counter[data-number]')];
						if (stats.length) {
							setTimeout(() => {
								stats.map((el) => new Stat(el));
							}, statDelay);
						}
					}

					panel.classList.add('stats-init');
				}
			}
		}
	};

	/**
	 * Opens the next tab.
	 */
	next = () => {
		const panelIndex = this.currentPanelIndex + 1 < this.panels.length ? this.currentPanelIndex + 1 : 0;
		this.open(this.tabs[panelIndex]);
	};

	/**
	 * Opens the previous tab.
	 */
	prev = () => {
		const panelIndex = this.currentPanelIndex - 1 > -1 ? this.currentPanelIndex - 1 : this.panels.length - 1;
		this.open(this.tabs[panelIndex]);
	};

	/**
	 * Event handler for tab click event.
	 * @param {Event} e - The click event object.
	 */
	handleTabClick = (e) => {
		const tab = e.currentTarget;
		if (this.useTabs()) {
			this.open(tab);
		} else if (tab.getAttribute('aria-selected') === 'true') {
			this.close(tab);
		} else {
			this.open(tab);
		}
	};

	/**
	 * Event handler for keydown event.
	 * @param {Event} e - The keydown event object.
	 */
	handleKeydown = (e) => {
		let flag = false;

		switch (e.key) {
			case 'ArrowLeft':
				if (!this.useVerticalNavigation()) {
					this.prev();
					flag = true;
				}
				break;

			case 'ArrowRight':
				if (!this.useVerticalNavigation()) {
					this.next();
					flag = true;
				}
				break;

			case 'ArrowUp':
				if (this.useVerticalNavigation()) {
					this.prev();
					flag = true;
				}
				break;

			case 'ArrowDown':
				if (this.useVerticalNavigation()) {
					this.next();
					flag = true;
				}
				break;

			case 'Home':
				this.open(this.tabs[0]);
				flag = true;
				break;

			case 'End':
				this.open(this.tabs[this.panels.length - 1]);
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			e.stopPropagation();
			e.preventDefault();
		}
	};

	/**
	 * Checks if vertical navigation should be used.
	 * @returns {boolean} - Whether to use vertical navigation.
	 */
	useVerticalNavigation = () => this.isVertical && this.useTabs();

	/**
	 * Checks if tabs should be used based on the current breakpoint.
	 * @returns {boolean} - Whether to use tabs.
	 */
	useTabs = () => embark.breakpoints.above(this.options.breakpoint);

	/**
	 * Checks if accordions should be used based on the current breakpoint.
	 * @returns {boolean} - Whether to use accordions.
	 */
	useAccordions = () => embark.breakpoints.below(this.options.breakpoint);

	/**
	 * Sets equal height for all tabpanels if the block has background-cover style.
	 * Only applies on screens above 850px.
	 */
	setEqualTabPanelHeight = () => {
		// Check if we're above 850px
		if (window.innerWidth <= 850) {
			// If below 850px, remove the inline style
			const tabpanelsContainer = this.el.querySelector('.wp-block-embark-tabs__tabpanels');
			if (tabpanelsContainer) {
				tabpanelsContainer.style.height = null;
			}
			return;
		}

		// Check if this tabs block has the background-cover style
		if (!this.el.classList.contains('is-style-background-cover')) {
			return;
		}

		// Get the tabpanels container
		const tabpanelsContainer = this.el.querySelector('.wp-block-embark-tabs__tabpanels');
		if (!tabpanelsContainer || !this.panels || this.panels.length === 0) {
			return;
		}

		// Temporarily remove height to get natural heights
		tabpanelsContainer.style.height = 'auto';

		// Get the maximum height from all panels
		let maxHeight = 0;
		this.panels.forEach((panel) => {
			const height = panel.offsetHeight;
			if (height > maxHeight) {
				maxHeight = height;
			}
		});

		// Set the max height on the container
		if (maxHeight > 0) {
			tabpanelsContainer.style.height = `${maxHeight}px`;
		}
	};

	/**
	 * Event handler for resize event.
	 */
	handleResize = () => {
		window.requestAnimationFrame(() => {
			if (this.useTabs()) {
				this.panels.forEach((panel) => {
					const inner = panel.querySelector('.wp-block-embark-tabpanel__inner');
					if (inner) {
						inner.style = null;
					}
				});
			}

			// Recalculate equal heights for background-cover style
			this.setEqualTabPanelHeight();
		});
	};

	maybeOpenTabByHash = () => {
		const hash = window.location.hash.slice(1);
		if (hash) {
			// Get the correct tab from this.tabs based on the aria-controls attribute matching the hash.
			const tab = this.tabs.find((el) => el.getAttribute('aria-controls') === hash);

			if (tab) {
				this.open(tab);
			}
		}
	};
}

document.addEventListener('DOMContentLoaded', () => {
	// Find all tabs blocks and initialize Tabs component for each block.
	const tabsBlocks = [...document.querySelectorAll('.wp-block-embark-tabs')];
	if (tabsBlocks.length) {
		tabsBlocks.map((el) => new Tabs(el));
	}
});
